import { User } from '../types';

/**
 * Valores canônicos do backend (app.models.UserRole).
 * - admin: gestão completa, inclusive usuários e flag de autoaprovação
 * - gerente: poderes operacionais de admin, sem gestão de usuários/flag
 * - analista / usuario
 */
export const ROLE = {
  ADMIN: 'admin',
  ANALISTA: 'analista',
  GERENTE: 'gerente',
  USUARIO: 'usuario',
} as const satisfies Record<string, User['role']>;

type RoleValue = (typeof ROLE)[keyof typeof ROLE];

const ROLE_ALIASES: Record<string, RoleValue> = {
  admin: ROLE.ADMIN,
  analista: ROLE.ANALISTA,
  gerente: ROLE.GERENTE,
  usuario: ROLE.USUARIO,
  'usuário': ROLE.USUARIO, // fallback defensivo de normalização
};

export const normalizeRole = (role: unknown): RoleValue | undefined => {
  if (typeof role !== 'string') return undefined;
  const normalized = role.trim().toLowerCase();
  return ROLE_ALIASES[normalized];
};

export const hasRole = (user: Pick<User, 'role'> | null | undefined, role: RoleValue): boolean =>
  normalizeRole(user?.role) === role;

export const isAdmin = (user: Pick<User, 'role'> | null | undefined): boolean =>
  hasRole(user, ROLE.ADMIN);

export const isAnalista = (user: Pick<User, 'role'> | null | undefined): boolean =>
  hasRole(user, ROLE.ANALISTA);

export const isUsuario = (user: Pick<User, 'role'> | null | undefined): boolean =>
  hasRole(user, ROLE.USUARIO);

export const isGerente = (user: Pick<User, 'role'> | null | undefined): boolean =>
  hasRole(user, ROLE.GERENTE);

/** Somente ADMIN — gestão de usuários e flag de autoaprovação. */
export const canManageUsers = (user: Pick<User, 'role'> | null | undefined): boolean =>
  isAdmin(user);

/** ADMIN ou GERENTE — poderes operacionais de administração. */
export const hasAdminCapabilities = (user: Pick<User, 'role'> | null | undefined): boolean =>
  isAdmin(user) || isGerente(user);

/** Usuário comum (sem poderes de analista/admin/gerente). */
export const isEndUser = (user: Pick<User, 'role'> | null | undefined): boolean =>
  isUsuario(user);

export const canManageTicketPriority = (user: Pick<User, 'role'> | null | undefined): boolean =>
  hasAdminCapabilities(user) || isAnalista(user);

/** Prioridade de compras temporariamente desativada na UI. */
export const canManagePurchasePriority = (_user: Pick<User, 'role'> | null | undefined): boolean =>
  false;
